/**
 * Geolocation Utility
 * Handles getting user location with fallback to cached position or Kochi, India
 */

const FALLBACK_LOCATION = {
  latitude: 9.9312,
  longitude: 76.2673,
  name: 'Kochi, India',
};

const GEOLOCATION_TIMEOUT = 15000; // 15 seconds
const CACHE_KEY = 'eve_last_known_location';
const CACHE_MAX_AGE = 24 * 60 * 60 * 1000; // 24 hours

export type FallbackReason = 'unsupported' | 'permission_denied' | 'position_unavailable' | 'timeout' | 'unknown';

export interface GeolocationResult {
  latitude: number;
  longitude: number;
  isFallback: boolean;
  fallbackReason?: FallbackReason;
  /**
   * Radius of the 95% confidence circle, in metres, as reported by the device.
   * A network-derived fix is not a fallback — it comes back through the normal
   * path — but can be hundreds of metres out, so anything that claims to know
   * where the user *is* has to check this rather than trust `isFallback` alone.
   * Absent on cached and fallback coordinates.
   */
  accuracy?: number;
}

interface CachedLocation {
  latitude: number;
  longitude: number;
  timestamp: number;
}

function getCachedLocation(): CachedLocation | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const cached: CachedLocation = JSON.parse(raw);
    if (typeof cached.latitude !== 'number' || typeof cached.longitude !== 'number' || typeof cached.timestamp !== 'number') {
      localStorage.removeItem(CACHE_KEY);
      return null;
    }
    if (Date.now() - cached.timestamp > CACHE_MAX_AGE) {
      localStorage.removeItem(CACHE_KEY);
      return null;
    }
    return cached;
  } catch {
    return null;
  }
}

function cacheLocation(latitude: number, longitude: number): void {
  try {
    const entry: CachedLocation = { latitude, longitude, timestamp: Date.now() };
    localStorage.setItem(CACHE_KEY, JSON.stringify(entry));
  } catch {
    // Storage full or unavailable — ignore
  }
}

function getFallbackCoords(): { latitude: number; longitude: number; source: 'cache' | 'default' } {
  const cached = getCachedLocation();
  if (cached) {
    return { latitude: cached.latitude, longitude: cached.longitude, source: 'cache' };
  }
  return { latitude: FALLBACK_LOCATION.latitude, longitude: FALLBACK_LOCATION.longitude, source: 'default' };
}

/**
 * Get user's current location using browser geolocation API
 * Falls back to last known cached position, then Kochi coordinates
 * Returns fallback info so callers can notify users
 */
export async function getUserLocation(): Promise<GeolocationResult> {
  if (!navigator.geolocation) {
    console.warn('[Geolocation] Browser does not support geolocation, using fallback:', FALLBACK_LOCATION.name);
    const { latitude, longitude } = getFallbackCoords();
    return { latitude, longitude, isFallback: true, fallbackReason: 'unsupported' };
  }

  try {
    const position = await new Promise<GeolocationPosition>((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(resolve, reject, {
        timeout: GEOLOCATION_TIMEOUT,
        enableHighAccuracy: false,
        maximumAge: 300000, // Accept cached position up to 5 minutes old
      });
    });

    const { latitude, longitude, accuracy } = position.coords;
    cacheLocation(latitude, longitude);

    console.log('[Geolocation] User location obtained:', { latitude, longitude, accuracy });
    return { latitude, longitude, isFallback: false, accuracy };
  } catch (error) {
    let fallbackReason: FallbackReason = 'unknown';

    if (error instanceof GeolocationPositionError) {
      switch (error.code) {
        case error.PERMISSION_DENIED:
          console.warn('[Geolocation] Permission denied, using fallback');
          fallbackReason = 'permission_denied';
          break;
        case error.POSITION_UNAVAILABLE:
          console.warn('[Geolocation] Position unavailable, using fallback');
          fallbackReason = 'position_unavailable';
          break;
        case error.TIMEOUT:
          console.warn('[Geolocation] Request timeout, using fallback');
          fallbackReason = 'timeout';
          break;
        default:
          console.warn('[Geolocation] Unknown error, using fallback');
      }
    } else {
      console.warn('[Geolocation] Error getting location, using fallback:', error);
    }

    const { latitude, longitude, source } = getFallbackCoords();
    if (source === 'cache') {
      console.log('[Geolocation] Using cached last-known location');
    }

    return { latitude, longitude, isFallback: true, fallbackReason };
  }
}

/**
 * Get an immediate location (cached or default) without waiting for GPS.
 * Use this to show the map instantly while GPS resolves in the background.
 */
export function getInstantLocation(): { latitude: number; longitude: number } {
  const { latitude, longitude } = getFallbackCoords();
  return { latitude, longitude };
}

/**
 * Haversine distance between two coordinates in kilometers.
 */
export function haversineDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 6371;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}
